// Inline SVG icons in the Lucide visual line.
//
// Centralized so any view can pull the same icon by name. Replace individual
// definitions previously scattered across views with imports from here.
// See DESIGN.md §8 for the icon-to-concept mapping.
//
// All icons default to 16x16 with stroke 1.5; pass `width`/`height` to override.

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const baseProps: SVGProps<SVGSVGElement> = {
  viewBox: "0 0 16 16",
  fill: "none",
  "aria-hidden": true,
};

export const SearchIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const CalendarIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <rect x="2" y="3.5" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 6.5h12M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const PlusIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const PencilIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 14l1-3 8-8 3 3-8 8-3 .5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M10 3l3 3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export const TrashIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 7v5M9.5 7v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const EyeIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M1 8s2.2-5 7-5 7 5 7 5-2.2 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export const XIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M10 3l-5 5 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronDownIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const MoreVerticalIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="3" r="1.2" fill="currentColor" />
    <circle cx="8" cy="8" r="1.2" fill="currentColor" />
    <circle cx="8" cy="13" r="1.2" fill="currentColor" />
  </svg>
);

export const ArrowDownUpIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M4 2v10m0 0l-2-2m2 2l2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 14V4m0 0l-2 2m2-2l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowUpIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M8 13V3M3 8l5-5 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowDownIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const BarChart3Icon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 14V4M6 14V2M10 14V7M14 14V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const HistoryIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 8a6 6 0 1 0 1.8-4.3M2 2v3.5h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 5v3.2l2 1.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SettingsIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M13.5 8a5.5 5.5 0 0 0-.1-1l1.4-1-1.3-2.3-1.7.5a5.5 5.5 0 0 0-1.7-1L10 1.5h-4l-.1 1.7a5.5 5.5 0 0 0-1.7 1l-1.7-.5L1.2 6l1.4 1a5.5 5.5 0 0 0 0 2L1.2 10l1.3 2.3 1.7-.5a5.5 5.5 0 0 0 1.7 1l.1 1.7h4l.1-1.7a5.5 5.5 0 0 0 1.7-1l1.7.5L15 10l-1.4-1c.07-.32.1-.66.1-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>
);

export const UsersIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M1.5 14c.5-2.5 2.5-4 4.5-4s4 1.5 4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M11 6.5a2 2 0 1 0 0-4M14.5 13.5c-.3-1.7-1.5-2.8-3-3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const UserIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2.5 14c.7-2.7 2.7-4.5 5.5-4.5s4.8 1.8 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const LogOutIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M7 2.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.5 5l3.5 3-3.5 3M13.5 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const BanknoteIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="8" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.5 5.5L5 7.5M12.5 5.5L11 7.5M3.5 10.5L5 8.5M12.5 10.5L11 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

export const MoneyCircleIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="7.2" cy="8.8" r="5.2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7.2 5.3v7M8.8 6.5c-.35-.45-.95-.72-1.65-.72-.95 0-1.7.53-1.7 1.28 0 1.92 3.55.85 3.55 2.75 0 .78-.75 1.4-1.8 1.4-.76 0-1.43-.3-1.85-.8" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.8 3.2H14v3.2M14 3.2l-3.1 3.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowLeftRightIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 5h11M10 2l3 3-3 3M14 11H3M6 14l-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CreditCardIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M1.5 6.5h13M3.5 10h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const NotebookPenIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 2.5h7.5a2 2 0 0 1 2 2V13a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3 5.5h9.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M11.5 9.5l1.5 1.5-3 1.5 1.5-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

export const CheckIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const AlertCircleIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const InfoCircleIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 7v4M8 5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const ShoppingBagIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 5h10l-.6 8.5H3.6L3 5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M5.5 6V4.5a2.5 2.5 0 0 1 5 0V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const ConsignmentIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="8" cy="3.1" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4.1 9.8V9a3.9 3.9 0 0 1 7.8 0v.8M4.3 9.2l1.4 1.1M11.7 9.2l-1.4 1.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m5.1 10.2 2.9-1.6 2.9 1.6v3.5L8 15.3l-2.9-1.6v-3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="m5.3 10.3 2.7 1.5 2.7-1.5M8 11.8v3.3" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
  </svg>
);

export const PackageIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 5l6-3 6 3v7l-6 3-6-3V5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M2.3 5L8 8l5.7-3M8 8v7" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

export const TagIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M2 3v4.5L8.5 14 14 8.5 7.5 2H3a1 1 0 0 0-1 1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="5" cy="5" r="1" fill="currentColor" />
  </svg>
);

export const RulerIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="m3 11 8-8 2 2-8 8H3v-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="m8 6 2 2M6 8l1.3 1.3M10 4l1.3 1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

export const MoreHorizontalIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <circle cx="3" cy="8" r="1.25" fill="currentColor" />
    <circle cx="8" cy="8" r="1.25" fill="currentColor" />
    <circle cx="13" cy="8" r="1.25" fill="currentColor" />
  </svg>
);

export const ReceiptIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 2.5h10v12l-2-1.2-2 1.2-2-1.2-2 1.2-2-1.2V2.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M5.5 6h5M5.5 9h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export const SplitPaymentIcon = (props: IconProps) => (
  <svg width="16" height="16" {...baseProps} {...props}>
    <path d="M3 3.5h3A2.5 2.5 0 0 1 8.5 6v4A2.5 2.5 0 0 0 11 12.5h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="m11 10.5 2 2-2 2M3 12.5h3A2.5 2.5 0 0 0 8.5 10V6A2.5 2.5 0 0 1 11 3.5h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m11 1.5 2 2-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
